import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signInWithPopup, 
  signOut as firebaseSignOut, 
  User as FirebaseUser 
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  serverTimestamp 
} from 'firebase/firestore';
import { auth, googleProvider, db, handleFirestoreError, OperationType } from '../firebase';
import { UserMember } from '../types';
import { requestAndEnableNotifications, testPublicationNotification, areNotificationsEnabled } from '../services/notificationService';

interface AuthContextType {
  currentUser: UserMember | null;
  isLoading: boolean;
  isMember: boolean;
  notificationsActive: boolean;
  isAuthModalOpen: boolean;
  authModalAction: string;
  openAuthModal: (action?: string) => void;
  closeAuthModal: () => void;
  signInWithGoogle: () => Promise<void>;
  subscribeAndSignInWithEmail: (email: string, name?: string) => Promise<{ success: boolean; error?: string }>;
  loginExistingSubscriber: (email: string) => Promise<{ success: boolean; error?: string }>;
  updateUserProfile: (name: string, photoURL: string) => Promise<{ success: boolean; error?: string }>;
  enableNotifications: () => Promise<{ granted: boolean; status: string }>;
  testNotification: () => void;
  logout: () => Promise<void>;
}

const LOCAL_STORAGE_KEY = 'literafan_member_session';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<UserMember | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState<boolean>(false);
  const [authModalAction, setAuthModalAction] = useState<string>('participar da comunidade');
  const [notificationsActive, setNotificationsActive] = useState<boolean>(() => areNotificationsEnabled());

  // Helper to sanitize doc ID from email
  const sanitizeEmailKey = (email: string) => email.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');

  // Load or sync subscriber status for a Firebase user
  const syncFirebaseUser = async (fbUser: FirebaseUser) => {
    if (!fbUser.email) return;
    const emailLower = fbUser.email.toLowerCase();
    const emailKey = sanitizeEmailKey(emailLower);

    try {
      // Check or add to newsletter_subscribers
      const subRef = doc(db, 'newsletter_subscribers', emailKey);
      const subSnap = await getDoc(subRef);

      if (!subSnap.exists()) {
        await setDoc(subRef, {
          email: emailLower,
          name: fbUser.displayName || 'Leitor Literafan',
          uid: fbUser.uid,
          notificationsEnabled: true,
          createdAt: serverTimestamp()
        }, { merge: true });
      }

      // Also ensure in users collection
      const userRef = doc(db, 'users', fbUser.uid);
      await setDoc(userRef, {
        uid: fbUser.uid,
        email: emailLower,
        displayName: fbUser.displayName || 'Leitor Literafan',
        role: emailLower === 'sevidiz.victordiniz@gmail.com' ? 'admin' : 'user',
        createdAt: serverTimestamp()
      }, { merge: true }).catch(() => {});

      const member: UserMember = {
        uid: fbUser.uid,
        email: emailLower,
        displayName: fbUser.displayName || emailLower.split('@')[0],
        photoURL: fbUser.photoURL || undefined,
        isNewsletterSubscriber: true,
        role: emailLower === 'sevidiz.victordiniz@gmail.com' ? 'admin' : 'user'
      };

      setCurrentUser(member);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(member));

      // Ativa notificações automaticamente ao entrar/se inscrever
      requestAndEnableNotifications().then(res => {
        if (res.granted) setNotificationsActive(true);
      });
    } catch (err) {
      console.warn('Error syncing subscriber status:', err);
      // Fallback local member object
      const fallbackMember: UserMember = {
        uid: fbUser.uid,
        email: emailLower,
        displayName: fbUser.displayName || emailLower.split('@')[0],
        photoURL: fbUser.photoURL || undefined,
        isNewsletterSubscriber: true,
        role: emailLower === 'sevidiz.victordiniz@gmail.com' ? 'admin' : 'user'
      };
      setCurrentUser(fallbackMember);
    }
  };

  useEffect(() => {
    // 1. Initial local storage check for email-based subscribers
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.email) {
          setCurrentUser(parsed);
        }
      } catch (_) {}
    }

    // 2. Firebase Auth Listener
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        await syncFirebaseUser(fbUser);
      } else {
        // If not in Firebase Auth, keep local subscriber if valid
        const localSaved = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (localSaved) {
          try {
            const parsed = JSON.parse(localSaved);
            if (parsed && parsed.email && parsed.uid?.startsWith('sub_')) {
              setCurrentUser(parsed);
            } else {
              setCurrentUser(null);
            }
          } catch (_) {
            setCurrentUser(null);
          }
        } else {
          setCurrentUser(null);
        }
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const openAuthModal = (action = 'participar da comunidade') => {
    setAuthModalAction(action);
    setIsAuthModalOpen(true);
  };

  const closeAuthModal = () => {
    setIsAuthModalOpen(false);
  };

  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      if (result.user) {
        await syncFirebaseUser(result.user);
        setIsAuthModalOpen(false);
      }
    } catch (error: any) {
      console.error('Google Sign In error:', error);
      throw error;
    }
  };

  // Subscribe via Email directly (without Google OAuth)
  const subscribeAndSignInWithEmail = async (email: string, name?: string): Promise<{ success: boolean; error?: string }> => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      return { success: false, error: 'Por favor, insira um e-mail válido.' };
    }

    const displayName = name?.trim() || trimmedEmail.split('@')[0];
    const emailKey = sanitizeEmailKey(trimmedEmail);

    try {
      const subRef = doc(db, 'newsletter_subscribers', emailKey);
      await setDoc(subRef, {
        email: trimmedEmail,
        name: displayName,
        uid: 'sub_' + emailKey,
        notificationsEnabled: true,
        source: 'modal',
        status: 'active',
        createdAt: serverTimestamp()
      }, { merge: true });

      // Dispara replicação instantânea via API backend
      fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail, name: displayName, source: 'modal' })
      }).catch(() => {});

      const member: UserMember = {
        uid: 'sub_' + emailKey,
        email: trimmedEmail,
        displayName: displayName,
        isNewsletterSubscriber: true,
        role: trimmedEmail === 'sevidiz.victordiniz@gmail.com' ? 'admin' : 'user'
      };

      setCurrentUser(member);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(member));
      setIsAuthModalOpen(false);

      // Ativa notificações automaticamente ao se inscrever
      requestAndEnableNotifications().then(res => {
        if (res.granted) setNotificationsActive(true);
      });

      return { success: true };
    } catch (err: any) {
      console.error('Error saving newsletter subscription:', err);
      // Tenta persistência via backend se o cliente direto falhou
      fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail, name: displayName, source: 'modal_fallback' })
      }).catch(() => {});

      const member: UserMember = {
        uid: 'sub_' + emailKey,
        email: trimmedEmail,
        displayName: displayName,
        isNewsletterSubscriber: true,
        role: trimmedEmail === 'sevidiz.victordiniz@gmail.com' ? 'admin' : 'user'
      };
      setCurrentUser(member);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(member));
      setIsAuthModalOpen(false);

      requestAndEnableNotifications().then(res => {
        if (res.granted) setNotificationsActive(true);
      });

      return { success: true };
    }
  };

  // Login existing subscriber by email
  const loginExistingSubscriber = async (email: string): Promise<{ success: boolean; error?: string }> => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      return { success: false, error: 'Por favor, insira um e-mail válido.' };
    }

    const emailKey = sanitizeEmailKey(trimmedEmail);
    try {
      const subRef = doc(db, 'newsletter_subscribers', emailKey);
      const subSnap = await getDoc(subRef);

      if (subSnap.exists()) {
        const data = subSnap.data();
        const member: UserMember = {
          uid: data.uid || ('sub_' + emailKey),
          email: data.email || trimmedEmail,
          displayName: data.name || trimmedEmail.split('@')[0],
          photoURL: data.photoURL || undefined,
          isNewsletterSubscriber: true,
          role: trimmedEmail === 'sevidiz.victordiniz@gmail.com' ? 'admin' : 'user'
        };
        setCurrentUser(member);
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(member));
        setIsAuthModalOpen(false);

        requestAndEnableNotifications().then(res => {
          if (res.granted) setNotificationsActive(true);
        });

        return { success: true };
      } else {
        // Automatically create subscription for user convenience
        return await subscribeAndSignInWithEmail(trimmedEmail);
      }
    } catch (err) {
      // Fallback
      return await subscribeAndSignInWithEmail(trimmedEmail);
    }
  };

  const updateUserProfile = async (name: string, photoURL: string): Promise<{ success: boolean; error?: string }> => {
    if (!currentUser) return { success: false, error: 'Usuário não conectado.' };

    const trimmedName = name.trim() || currentUser.displayName;
    const emailLower = currentUser.email.toLowerCase();
    const emailKey = sanitizeEmailKey(emailLower);

    try {
      const subRef = doc(db, 'newsletter_subscribers', emailKey);
      await setDoc(subRef, {
        name: trimmedName,
        photoURL: photoURL.trim() || ''
      }, { merge: true }).catch(() => {});

      if (!currentUser.uid.startsWith('sub_')) {
        const userRef = doc(db, 'users', currentUser.uid);
        await setDoc(userRef, {
          displayName: trimmedName,
          photoURL: photoURL.trim() || ''
        }, { merge: true }).catch(() => {});
      }

      const updatedMember: UserMember = {
        ...currentUser,
        displayName: trimmedName,
        photoURL: photoURL.trim() || undefined
      };

      setCurrentUser(updatedMember);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedMember));
      return { success: true };
    } catch (err: any) {
      console.error('Error updating profile:', err);
      const updatedMember: UserMember = {
        ...currentUser,
        displayName: trimmedName,
        photoURL: photoURL.trim() || undefined
      };
      setCurrentUser(updatedMember);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedMember));
      return { success: true };
    }
  };

  const enableNotifications = async () => {
    const res = await requestAndEnableNotifications();
    if (res.granted) {
      setNotificationsActive(true);
    }
    return res;
  };

  const testNotification = () => {
    testPublicationNotification();
  };

  const logout = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (_) {}
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    setCurrentUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isLoading,
        isMember: Boolean(currentUser?.isNewsletterSubscriber),
        notificationsActive,
        isAuthModalOpen,
        authModalAction,
        openAuthModal,
        closeAuthModal,
        signInWithGoogle,
        subscribeAndSignInWithEmail,
        loginExistingSubscriber,
        updateUserProfile,
        enableNotifications,
        testNotification,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
